/**
 * Theme catalog for the Fias plugin builder.
 * Provides a browsable library of named themes that plugins can use.
 */

import type { FiasTheme } from '../types';
import { DARK_THEME, LIGHT_THEME } from '../theme';
import { DAISY_THEMES } from './catalog-data';

export interface ThemeCatalogEntry {
  id: string;
  name: string;
  source: 'fias' | 'daisy' | 'community' | 'ai' | 'none';
  mode: 'light' | 'dark';
  theme: FiasTheme;
  preview: {
    colors: [string, string, string, string]; // [primary, secondary, bg, accent]
  };
}

/** Special AI auto-selection entry — theme is a neutral placeholder */
const AI_AUTO_ENTRY: ThemeCatalogEntry = {
  id: 'ai-auto',
  name: "AI's Choice",
  source: 'ai',
  mode: 'dark',
  theme: DARK_THEME, // placeholder; AI picks the real theme at runtime
  preview: {
    colors: ['#8b5cf6', '#ec4899', '#1e1e2e', '#06b6d4'],
  },
};

/** No theme — AI builds the look and feel from scratch */
const NO_THEME_ENTRY: ThemeCatalogEntry = {
  id: 'no-theme',
  name: 'Custom from Scratch',
  source: 'none',
  mode: 'dark',
  theme: DARK_THEME, // placeholder; AI creates its own design
  preview: {
    colors: ['#f472b6', '#38bdf8', '#1a1a2e', '#a78bfa'],
  },
};

const FIAS_ENTRIES: ThemeCatalogEntry[] = [
  {
    id: 'fias-dark',
    name: 'Fias Dark',
    source: 'fias',
    mode: 'dark',
    theme: DARK_THEME,
    preview: {
      colors: [
        DARK_THEME.colors.primary,
        DARK_THEME.colors.secondary,
        DARK_THEME.colors.background,
        DARK_THEME.colors.accent,
      ],
    },
  },
  {
    id: 'fias-light',
    name: 'Fias Light',
    source: 'fias',
    mode: 'light',
    theme: LIGHT_THEME,
    preview: {
      colors: [
        LIGHT_THEME.colors.primary,
        LIGHT_THEME.colors.secondary,
        LIGHT_THEME.colors.background,
        LIGHT_THEME.colors.accent,
      ],
    },
  },
];

/** Full theme catalog: Fias originals + special entries + DaisyUI themes */
export const THEME_CATALOG: ThemeCatalogEntry[] = [
  ...FIAS_ENTRIES,
  AI_AUTO_ENTRY,
  NO_THEME_ENTRY,
  ...DAISY_THEMES,
];

/** Look up a theme by ID. Returns undefined if not found. */
export function getThemeById(id: string): ThemeCatalogEntry | undefined {
  return THEME_CATALOG.find((t) => t.id === id);
}
